/**
 * Lane C0-OUTFIT2X: small ornaments drawn natively at the figure grid — the man's gold hair crown with its jade pin,
 * frog buttons, a jade disc pendant with a red tassel; the woman's hairpin with a plum flower and dangling beads and her
 * jade waist pendant (禁步) with a long tassel. Each is a stamp of material letters; its light is worked out from its
 * own shape (lit edge toward the light, shade away and under), so it is lit right on every view.
 */
import type { Layer, Piece } from '../figure';
import type { HairAnchors } from '../hair';
import type { Skeleton } from '../skeleton';
import type { NativeCtx } from './native';
import { type Mat, mat, tone } from './paint';

/** A stamp: rows of material letters (`.` empty); `ax`/`ay` the cell laid on the anchor. Upper case: a step darker. */
interface Stamp {
  rows: readonly string[];
  ax: number;
  ay: number;
}

const GOLD = mat('#d6a640', 'gold');
const JADE = mat('#6cc0a0', 'jade');
const JADE_W = mat('#e4ece0', 'jade');
const RED = mat('#b8283a', 'silk');
const PINK = mat('#e87a96', 'silk');
const INK = mat('#2a1c22', 'lacquer');

const MATS: Record<string, Mat> = { g: GOLD, j: JADE, w: JADE_W, r: RED, p: PINK, k: INK };

/** Lays a stamp with its light worked out from its shape. `flip` mirrors the shape (not the light). */
function layStamp(c: NativeCtx, s: Stamp, X: number, Y: number, dz = 0.001, flip = false): void {
  const H = s.rows.length;
  const W = Math.max(...s.rows.map((r) => r.length));
  const ch = (u: number, v: number) => {
    if (v < 0 || v >= H || u < 0 || u >= W) return '.';
    return s.rows[v]![flip ? W - 1 - u : u] ?? '.';
  };
  const key = (q: string) => q.toLowerCase();
  for (let v = 0; v < H; v++)
    for (let u = 0; u < W; u++) {
      const q = ch(u, v);
      if (q === '.') continue;
      const m = MATS[key(q)];
      if (!m) continue;
      const same = (du: number, dv: number) => key(ch(u + du, v + dv)) === key(q);
      let lv = q === key(q) ? 0 : -1;
      if (q === '*') lv = 2;
      if (!same(c.light, 0)) lv += 1;
      else if (!same(-c.light, 0)) lv -= 1;
      if (!same(0, -1) && lv >= 0) lv = Math.min(2, lv + 1);
      if (!same(0, 1) && lv <= 0) lv -= 1;
      c.lay(X + u - (flip ? W - 1 - s.ax : s.ax), Y + v - s.ay, tone(m.ramp, lv), dz);
    }
}

// ── stamps (canvas pixels: two per art pixel) ──

/** 发冠: a small gold crown over the topknot, a white jade pin through it. */
const CROWN: Stamp = {
  rows: ['...g..g...', '..gg..gg..', '..gggggg..', 'wwgggggGww', '..gGGGGg..', '...GGGG...'],
  ax: 5,
  ay: 5,
};
const CROWN_SIDE: Stamp = {
  rows: ['..g.g..', '.gg.gg.', '.ggggg.', 'wwggggww', '.gGGGg.', '..GGG..'],
  ax: 3,
  ay: 5,
};
/** From behind, the pin's two ends either side of the knot. */
const CROWN_BACK: Stamp = { rows: ['ww......ww'], ax: 5, ay: 1 };

/** 盘扣: a frog button, two loops and a knot. */
const FROG: Stamp = { rows: ['.gg.gg.', 'g..g..g', '.gg.gg.'], ax: 3, ay: 1 };
const FROG_SIDE: Stamp = { rows: ['.gg.', 'g..g', '.gg.'], ax: 2, ay: 1 };

/** 玉佩: a red cord knot, a jade disc with its hole, a gold cap and a red tassel. */
const PENDANT: Stamp = {
  rows: [
    '..rr..',
    '.rRRr.',
    '..rr..',
    '..r...',
    '.jjjj.',
    'jj..jj',
    'jJ..jj',
    '.JJjj.',
    '..gg..',
    '..rr..',
    '.rrrr.',
    '.rRrr.',
    'rrRrRr',
    'rRrRrr',
    'r.r.r.',
  ],
  ax: 2,
  ay: 0,
};

/** 禁步: the woman's longer waist pendant, a jade plaque, beads and a long tassel. */
const PENDANT_LONG: Stamp = {
  rows: [
    '..rr...',
    '.rRRr..',
    '..rr...',
    '..r....',
    '..g....',
    '.wwww..',
    'wwjjww.',
    'wjjjjw.',
    'wwjjww.',
    '.wWWw..',
    '..g....',
    '..r....',
    '..g....',
    '.rrr...',
    '.rRrr..',
    'rrRrRr.',
    'rRrRrr.',
    'rrRrRr.',
    'r.r.r..',
  ],
  ax: 2,
  ay: 0,
};

/** 步摇: a gold pin with a plum flower on its head and two strings of beads hanging from it. */
const HAIRPIN: Stamp = {
  rows: [
    '...pp.....',
    '..pPPp....',
    '.pPggPp...',
    '..pPPpg...',
    '...pp.g...',
    '..g...g.g.',
    '.g....r.g.',
    'g.....g.r.',
    '......r.g.',
    '........r.',
  ],
  ax: 4,
  ay: 2,
};

// ── where they go ──

const shoulderSide = (sk: Skeleton) => Math.sign(sk.joints.shoulderA.x - sk.joints.chest.x) || 1;

/**
 * A piece that is only a native ornament: one all-but-transparent art pixel at its anchor (so it has a place in the
 * frame without showing), the stamp laid over it at full resolution.
 */
function ornament(ax: number, ay: number, z: number, paint: (c: NativeCtx) => void): Piece {
  return {
    z,
    draw: (g) => {
      g.fillStyle = 'rgba(214, 166, 64, 0.004)';
      g.fillRect(Math.round(ax), Math.round(ay), 1, 1);
    },
    native: paint,
  };
}

export interface OrnamentSet {
  crown?: boolean;
  frogs?: boolean;
  pendant?: 'hip' | 'long';
  hairpin?: boolean;
}

/** The ornaments of one figure, as a layer. */
export function ornamentLayer(o: OrnamentSet): Layer {
  return {
    id: 'ornaments2x',
    label: 'เครื่องประดับ',
    native: true,
    pieces(sk, fit) {
      const j = sk.joints;
      const h = j.head;
      const out: Piece[] = [];
      const anchors: HairAnchors = fit.anchors;
      if (o.crown) {
        const [cx, cy] = anchors.crown;
        const back = sk.back;
        const st = back ? CROWN_BACK : sk.view === 'side' ? CROWN_SIDE : CROWN;
        out.push(
          ornament(h.x + cx, h.y + cy, h.z + 0.04, (c) =>
            layStamp(c, st, c.X(h.x + cx) + 1, c.Y(h.y + cy) + 1),
          ),
        );
      }
      if (o.hairpin) {
        const [bx, by] = anchors.buns[0] ?? anchors.crown;
        out.push(
          ornament(h.x + bx, h.y + by, h.z + 0.045, (c) =>
            layStamp(c, HAIRPIN, c.X(h.x + bx) + 1, c.Y(h.y + by) + 1, 0.001, sk.back),
          ),
        );
      }
      if (sk.back) return out;
      const side = shoulderSide(sk);
      const front = j.chest.z + 0.09;
      if (o.frogs) {
        const sw = Math.abs(j.shoulderA.x - j.shoulderB.x) / 2;
        const fx = j.chest.x + (sk.view === 'side' ? 1.5 : side * Math.max(1.5, sw * 0.45));
        for (const dy of [2, 5]) {
          const fy = j.chest.y + dy;
          out.push(
            ornament(fx, fy, front, (c) => layStamp(c, sk.view === 'side' ? FROG_SIDE : FROG, c.X(fx) + 1, c.Y(fy) + 1)),
          );
        }
      }
      if (o.pendant === 'hip') {
        // On the weapon side's hip, in front of the robe when that hip is toward the camera.
        const near = sk.square || j.hipA.z >= j.hipB.z - 0.01;
        if (near) {
          const px = j.pelvis.x + (j.hipA.x - j.pelvis.x) * 1.4 + (sk.view === 'side' ? 1 : 0);
          const py = j.waist.y + 1;
          out.push(ornament(px, py + 2, front, (c) => layStamp(c, PENDANT, c.X(px) + 1, c.Y(py) + 1)));
        }
      }
      if (o.pendant === 'long') {
        const t = 0.45;
        const px = j.chest.x + (j.waist.x - j.chest.x) * t + (sk.square ? 0 : sk.view === 'side' ? 2 : 1.5);
        const py = j.chest.y + (j.waist.y - j.chest.y) * t + 2;
        out.push(ornament(px, py + 2, front, (c) => layStamp(c, PENDANT_LONG, c.X(px) + 1, c.Y(py) + 1)));
      }
      return out;
    },
  };
}

export { INK };
